import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScanLine, CalendarClock, FolderArchive, Users, FileSignature, MessageCircle, ArrowRight, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PageHeader } from "@/components/PageHeader";
import { api, daysLeft, fmtDate } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, to, id }) => (
  <Link to={to} className="lift rounded-2xl border bg-white p-5 block" data-testid={`stat-${id}`}>
    <Icon className="h-5 w-5 text-[#1E3A8A]" />
    <p className="mt-4 text-3xl font-display font-semibold">{value}</p>
    <p className="text-sm text-slate-500">{label}</p>
  </Link>
);

const dueLabel = (d) => {
  const n = daysLeft(d);
  return n < 0 ? `scaduta da ${-n} gg` : n === 0 ? "oggi" : `tra ${n} gg`;
};

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setS(data));
  }, []);
  if (!s) return <div className="h-40 rounded-3xl bg-slate-100 animate-pulse" />;

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="Panoramica" title="Cosa c'è da fare" desc="Le tue pratiche, scadenze e documenti in un colpo d'occhio." />
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Link to="/app/analizza" className="md:col-span-2 lg:row-span-2 lift rounded-3xl bg-[#0B0F19] text-white p-8 relative overflow-hidden noise" data-testid="dashboard-scan-cta">
          <ScanLine className="h-8 w-8 text-[#E07A5F]" />
          <h2 className="mt-6 text-2xl md:text-3xl font-semibold tracking-tight max-w-sm">Hai ricevuto una lettera? Scatta una foto.</h2>
          <p className="mt-3 text-slate-300 max-w-sm">Ti diciamo cos'è, entro quando rispondere e se puoi farlo da solo.</p>
          <span className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[#0B0F19] px-5 py-2.5 text-sm font-medium">Analizza un documento <ArrowRight className="h-4 w-4" /></span>
        </Link>
        <Stat icon={CalendarClock} label="Scadenze aperte" value={s.deadlines_open} to="/app/scadenze" id="deadlines" />
        <Stat icon={FolderArchive} label="Documenti" value={s.docs} to="/app/archivio" id="docs" />
        <Stat icon={Users} label="Profili famiglia" value={s.profiles} to="/app/famiglia" id="profiles" />
        <Stat icon={FileSignature} label="Lettere" value={s.letters} to="/app/lettere" id="letters" />
      </div>

      {s.overdue > 0 && (
        <div className="mt-6 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800" data-testid="dashboard-overdue-alert">
          <AlertCircle className="h-5 w-5" /> Hai {s.overdue} scadenze già passate. <Link to="/app/scadenze" className="underline ml-auto">Controlla</Link>
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-3xl border bg-white p-6">
          <div className="flex items-center justify-between"><h3 className="font-medium text-lg">Prossimi 30 giorni</h3><Link to="/app/scadenze" className="text-sm text-[#1E3A8A]" data-testid="dashboard-all-deadlines">Tutte</Link></div>
          {s.upcoming.length === 0 && <p className="mt-4 text-sm text-slate-500">Nessuna scadenza in arrivo. Respira.</p>}
          <ul className="mt-4 divide-y">
            {s.upcoming.map((d) => (
              <li key={d.deadline_id} className="py-3 flex items-center justify-between gap-4" data-testid={`dashboard-deadline-${d.deadline_id}`}>
                <div><p className="font-medium">{d.title}</p><p className="text-xs text-slate-500">{d.category} · {fmtDate(d.due_date)}</p></div>
                <span className={`text-xs rounded-full px-2.5 py-1 ${daysLeft(d.due_date) < 0 ? "bg-red-50 text-red-700" : daysLeft(d.due_date) <= 7 ? "bg-amber-50 text-amber-700" : "bg-slate-100"}`}>{dueLabel(d.due_date)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="space-y-6">
          {s.doc_limit && (
            <div className="rounded-3xl border bg-white p-6" data-testid="dashboard-doc-limit">
              <p className="text-sm text-slate-500">Piano Free</p>
              <p className="mt-1 font-medium">{s.docs} di {s.doc_limit} documenti</p>
              <Progress value={(s.docs / s.doc_limit) * 100} className="mt-3" />
              <Button asChild size="sm" className="mt-4 rounded-full" data-testid="dashboard-upgrade-btn"><Link to="/app/pro">Passa a Pro</Link></Button>
            </div>
          )}
          <Link to="/app/chat" className="lift block rounded-3xl border bg-[#E07A5F]/10 border-[#E07A5F]/30 p-6" data-testid="dashboard-chat-cta">
            <MessageCircle className="h-6 w-6 text-[#E07A5F]" />
            <p className="mt-3 font-medium">Chiedi a Burocrazy</p>
            <p className="text-sm text-slate-600">"Come faccio la 104 per mia madre?"</p>
          </Link>
          {s.recent_analyses.length > 0 && (
            <div className="rounded-3xl border bg-white p-6">
              <h3 className="font-medium">Ultime analisi</h3>
              {s.recent_analyses.map((a) => <p key={a.analysis_id} className="mt-2 text-sm text-slate-600 truncate">{a.result?.titolo || a.filename}</p>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
